import axios from '../utils/axios'
import { userMessageAtom, userMessageStore } from '../atoms/userMessageAtom'
import { AxiosError, AxiosResponse } from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import {
    ApiContract,
    DeleteApiContract,
    GetApiContract,
    PatchApiContract,
    PostApiContract,
    PutApiContract,
} from '@finance-lab/utils/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

interface ApiError extends Error {
    status?: number
    error?: Error
}

class ApiBase {
    #baseUrl = API_BASE_URL
    #methods = { GET: 'GET', POST: 'POST', PUT: 'PUT', DELETE: 'DELETE', PATCH: 'PATCH', HEAD: 'HEAD' } as const

    constructor() {
        if (!this.#baseUrl) {
            throw new Error('API_BASE_URL is not set in .env')
        }
        // make sure we don't instantiate this class directly
        if (new.target === ApiBase) {
            throw new TypeError('Cannot construct Abstract instances directly')
        }
    }

    _getRequest<T extends GetApiContract = never>(
        endpoint: string,
        { queryParameters }: { queryParameters?: T['QueryParameters'] } = {},
    ) {
        return this._request<T>(this.#methods.GET, endpoint, { queryParameters })
    }

    async _postRequest<T extends PostApiContract = never>(endpoint: string, data: T['Body']) {
        return this._request<T>(this.#methods.POST, endpoint, { data })
    }

    async _putRequest<C extends PutApiContract>(endpoint: string, data: C['Body']) {
        return this._request<C>(this.#methods.PUT, endpoint, { data })
    }

    async _deleteRequest<C extends DeleteApiContract = never>(
        endpoint: string,
        { queryParameters }: { queryParameters?: C['QueryParameters'] } = {},
    ) {
        return this._request<C>(this.#methods.DELETE, endpoint, { queryParameters })
    }

    async _patchRequest<C extends PatchApiContract = never>(
        endpoint: string,
        data: C['Body'],
        queryParameters: C['QueryParameters'] = {},
    ) {
        return this._request<C>(this.#methods.PATCH, endpoint, { data, queryParameters })
    }

    async _headRequest(endpoint: string) {
        return this._requestHeaders(this.#methods.HEAD, endpoint)
    }

    /**
     * Make a request to the server.
     *
     * @param {*} method, e.g. 'GET', 'POST', 'PUT', 'DELETE'
     * @param {*} endpoint, e.g. '/password
     * @param {*} data payload for POST and PUT requests
     * @param {*} queryParameters query parameters to append to the endpoint
     * @returns response from the server
     */
    async _request<C extends ApiContract>(
        method: C['Method'],
        endpoint: string,
        {
            data = null,
            queryParameters = {},
        }: {
            data?: C['Body'] | null
            queryParameters?: C['QueryParameters']
        } = {},
    ) {
        data = data ? data : {}
        const queryParametersString = new URLSearchParams(
            Object.fromEntries(
                Object.entries(queryParameters)
                    .filter(([, value]) => value !== undefined)
                    .map(([key, value]) => [key, value.toString()]),
            ),
        ).toString()
        if (queryParametersString) {
            endpoint += '?' + queryParametersString
        }

        try {
            const response = await this._requestWithAxios<C>(method, this.#baseUrl + endpoint, data)

            return response
        } catch (err) {
            if (!(err instanceof Error)) {
                userMessageStore.set(userMessageAtom, {
                    type: 'error',
                    errorCode: null,
                    message: 'Ett okänt fel inträffade.',
                })
                throw err
            }

            if ('status' in err) {
                userMessageStore.set(userMessageAtom, {
                    type: 'error',
                    errorCode: err.status as number,
                    message: err.toString(),
                })
            }

            throw err
        }
    }

    /**
     * Make a request and only return the headers.
     */
    async _requestHeaders(
        method: string,
        endpoint: string,
        {
            data = null,
            queryParameters = {},
        }: { data?: Record<string, unknown> | null; queryParameters?: Record<string, string> } = {},
    ) {
        data = data ? data : null
        queryParameters = queryParameters ? queryParameters : {}
        const queryParametersString = new URLSearchParams(queryParameters).toString()
        if (queryParametersString) {
            endpoint += '?' + queryParametersString
        }
        return this._requestHeadersWithAxios(method, this.#baseUrl + endpoint, data)
    }

    /**
     * For now, this defers to the serverApi module. Once all calls have been migrated to
     * this class hierarchy, we can remove the serverApi module.
     *
     * @param {*} method http method, e.g. 'GET', 'POST', 'PUT', 'DELETE'
     * @param {*} url full url for the request
     * @param {*} data payload for POST and PUT requests
     * @returns response from the server
     */
    async _requestWithAxios<C extends ApiContract>(method: C['Method'], url: string, data: C['Body'] | null = null) {
        const response = await this._requestBodyAndHeadersWithAxios(data, url, method)

        return response.data
    }

    /**
     * For now, this defers to the serverApi module. Once all calls have been migrated to
     * this class hierarchy, we can remove the serverApi module.
     */
    async _requestHeadersWithAxios(method: string, url: string, data: Record<string, unknown> | null = null) {
        const response = await this._requestBodyAndHeadersWithAxios(data, url, method)
        return response.headers
    }

    async _requestBodyAndHeadersWithAxios<C extends ApiContract>(
        data: C['Body'],
        url: string,
        method: C['Method'],
    ): Promise<AxiosResponse<C['ResponseJson']>> {
        const headers = {
            'Content-Type': 'application/json',
        }
        const authSession = await fetchAuthSession()
        const idToken = authSession.tokens?.idToken?.toString()

        if (!idToken) {
            console.log('Failed to get idToken, will do an unauthenticated request.')
        }

        try {
            const response = await axios({
                method,
                url,
                headers: {
                    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                    ...headers,
                },
                data,
            })
            return response
        } catch (error) {
            if (!(error instanceof Error)) {
                console.error(error)
                throw new Error('Unknown error')
            }

            if (!(error instanceof AxiosError)) {
                throw error
            }

            if (error.response) {
                // Handle 4xx errors differently
                if (error.response.status >= 400 && error.response.status < 500) {
                    // Return or handle 400 errors as part of the normal flow
                    console.warn('Client error (4xx):', error.response.status, error.response.data)
                    const err: ApiError = new Error(error.response.data.message)
                    err.status = error.response.status
                    err.error = error

                    throw err
                }
                // For 5xx or other errors, throw an error
                const err: ApiError = new Error(`Server error! status: ${error.response.status}`)
                err.status = error.response.status

                throw err
            } else if (error.request) {
                throw new Error('No response received from server')
            } else {
                throw new Error(error.message)
            }
        }
    }

    get _methods() {
        return this.#methods
    }
}

export default ApiBase
